import { Component, OnInit, OnDestroy, EnvironmentInjector, inject, runInInjectionContext } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { Subscription } from 'rxjs';
import { MACHINING_MODULES } from './machining.data';
import { MachiningModule, ModuleProgress, UserProgress, QuizQuestion, Machine, ExerciseProgress } from './machining.model';

type AppView = 'overview' | 'module' | 'quiz';

@Component({
    selector: 'app-machining',
    templateUrl: './machining.component.html',
    styleUrls: ['./machining.component.scss'],
    standalone: false
})
export class MachiningComponent implements OnInit, OnDestroy {
  private injector = inject(EnvironmentInjector);
  modules = MACHINING_MODULES;
  currentView: AppView = 'overview';
  selectedModule: MachiningModule | null = null;
  selectedTrack: Machine = 'lathe';

  userProgress: UserProgress = {};
  userId: string | null = null;

  currentQuestionIndex = 0;
  selectedAnswerIndex: number | null = null;
  showExplanation = false;
  quizAnswers: number[] = [];
  quizCompleted = false;
  quizScore = 0;

  activeVideoId: string | null = null;
  activeVideoTitle = '';

  // Practice exercise state
  notesDrafts: { [exerciseId: string]: string } = {};
  expandedNotes: { [exerciseId: string]: boolean } = {};
  private notesTimeouts: { [exerciseId: string]: ReturnType<typeof setTimeout> } = {};

  private authSub?: Subscription;
  private progressSub?: Subscription;

  constructor(
    private auth: AngularFireAuth,
    private db: AngularFireDatabase
  ) {}

  private inCtx<T>(fn: () => T): T {
    return runInInjectionContext(this.injector, fn);
  }

  ngOnInit() {
    this.authSub = this.auth.user.subscribe(user => {
      this.userId = user?.uid ?? null;
      if (this.userId) {
        this.loadProgress();
      } else {
        this.userProgress = {};
      }
    });
  }

  ngOnDestroy() {
    this.authSub?.unsubscribe();
    this.progressSub?.unsubscribe();
    Object.values(this.notesTimeouts).forEach(t => clearTimeout(t));
  }

  private loadProgress() {
    if (!this.userId) return;
    this.progressSub?.unsubscribe();
    this.progressSub = this.inCtx(() => this.db
      .object<UserProgress>(`machining-progress/${this.userId}`)
      .valueChanges())
      .subscribe(progress => {
        this.userProgress = progress || {};
      });
  }

  selectTrack(track: Machine) {
    this.selectedTrack = track;
  }

  get trackModules(): MachiningModule[] {
    return this.modules
      .filter(m => (m.machine === this.selectedTrack || !m.machine) && !m.isBranchPath)
      .sort((a, b) => a.number - b.number);
  }

  get trackProgressPercent(): number {
    const main = this.trackModules;
    if (!main.length) return 0;
    let points = 0;
    const maxPoints = main.length * 2;
    main.forEach(m => {
      const p = this.userProgress[m.id];
      if (p?.visited) points++;
      if (p?.quizCompleted) points++;
    });
    return Math.round((points / maxPoints) * 100);
  }

  get trackProgressLabel(): string {
    const main = this.trackModules;
    const completed = main.filter(m => this.userProgress[m.id]?.quizCompleted).length;
    return `${completed} / ${main.length} modules complete`;
  }

  getModuleProgress(moduleId: string): ModuleProgress | null {
    return this.userProgress[moduleId] || null;
  }

  isModuleUnlocked(module: MachiningModule): boolean {
    if (!module.unlockAfterModule) return true;
    return !!this.userProgress[module.unlockAfterModule]?.visited;
  }

  getModuleStatus(module: MachiningModule): 'locked' | 'available' | 'visited' | 'completed' {
    if (!this.isModuleUnlocked(module)) return 'locked';
    const p = this.userProgress[module.id];
    if (!p?.visited) return 'available';
    if (p?.quizCompleted) return 'completed';
    return 'visited';
  }

  openModule(module: MachiningModule) {
    if (!this.isModuleUnlocked(module)) return;
    this.selectedModule = module;
    this.currentView = 'module';
    this.activeVideoId = null;
    this.resetQuiz();
    this.markVisited(module.id);
    this.populateNotesDrafts(module.id);
    window.scrollTo(0, 0);
  }

  private populateNotesDrafts(moduleId: string) {
    this.notesDrafts = {};
    this.expandedNotes = {};
    const moduleProgress = this.userProgress[moduleId];
    if (moduleProgress?.exercises) {
      Object.entries(moduleProgress.exercises).forEach(([exId, exProgress]) => {
        this.notesDrafts[exId] = exProgress.notes || '';
      });
    }
  }

  toggleNotesExpanded(exerciseId: string) {
    this.expandedNotes[exerciseId] = !this.expandedNotes[exerciseId];
  }

  isNotesExpanded(exerciseId: string): boolean {
    return !!this.expandedNotes[exerciseId] || !!(this.notesDrafts[exerciseId]?.trim());
  }

  exerciseNumberLabel(index: number): string {
    const n = index + 1;
    return n < 10 ? `0${n}` : `${n}`;
  }

  private markVisited(moduleId: string) {
    if (!this.userId) return;
    const existing = this.userProgress[moduleId];
    if (existing?.visited) return;
    this.inCtx(() => this.db.object(`machining-progress/${this.userId}/${moduleId}`).update({
      visited: true,
      lastVisited: Date.now()
    }));
  }

  goToOverview() {
    this.currentView = 'overview';
    this.selectedModule = null;
    this.activeVideoId = null;
    this.resetQuiz();
    window.scrollTo(0, 0);
  }

  openVideo(youtubeId: string | undefined, title: string, searchQuery: string) {
    if (youtubeId) {
      this.activeVideoId = youtubeId;
      this.activeVideoTitle = title;
    } else {
      const url = `https://www.youtube.com/results?search_query=${encodeURIComponent(searchQuery)}`;
      window.open(url, '_blank');
    }
  }

  closeVideo() {
    this.activeVideoId = null;
    this.activeVideoTitle = '';
  }

  getYouTubeUrl(searchQuery: string): string {
    return `https://www.youtube.com/results?search_query=${encodeURIComponent(searchQuery)}`;
  }

  startQuiz() {
    if (!this.selectedModule?.quiz?.length) return;
    this.activeVideoId = null;
    this.activeVideoTitle = '';
    this.currentView = 'quiz';
    this.resetQuiz();
    window.scrollTo(0, 0);
  }

  selectAnswer(index: number) {
    if (this.showExplanation) return;
    this.selectedAnswerIndex = index;
  }

  submitAnswer() {
    if (this.selectedAnswerIndex === null) return;
    this.quizAnswers.push(this.selectedAnswerIndex);
    this.showExplanation = true;
  }

  nextQuestion() {
    if (!this.selectedModule) return;
    if (this.currentQuestionIndex < this.selectedModule.quiz.length - 1) {
      this.currentQuestionIndex++;
      this.selectedAnswerIndex = null;
      this.showExplanation = false;
    } else {
      this.finishQuiz();
    }
  }

  private finishQuiz() {
    if (!this.selectedModule) return;
    const questions = this.selectedModule.quiz;
    const correct = questions.filter((q, i) => this.quizAnswers[i] === q.correctIndex).length;
    this.quizScore = Math.round((correct / questions.length) * 100);
    this.quizCompleted = true;

    if (this.userId) {
      const existing = this.userProgress[this.selectedModule.id];
      const shouldSave = !existing?.quizCompleted || this.quizScore > (existing?.quizScore ?? 0);
      if (shouldSave) {
        this.inCtx(() => this.db.object(`machining-progress/${this.userId}/${this.selectedModule.id}`).update({
          quizCompleted: true,
          quizScore: this.quizScore,
          visited: true,
          lastVisited: Date.now()
        }));
      }
    }
  }

  backToModule() {
    this.currentView = 'module';
    this.resetQuiz();
    window.scrollTo(0, 0);
  }

  get currentQuestion(): QuizQuestion | null {
    if (!this.selectedModule) return null;
    return this.selectedModule.quiz[this.currentQuestionIndex] || null;
  }

  get isCurrentAnswerCorrect(): boolean {
    if (this.selectedAnswerIndex === null || !this.currentQuestion) return false;
    return this.selectedAnswerIndex === this.currentQuestion.correctIndex;
  }

  isAnswerCorrectForIndex(questionIndex: number): boolean {
    if (!this.selectedModule) return false;
    return this.quizAnswers[questionIndex] === this.selectedModule.quiz[questionIndex].correctIndex;
  }

  getScoreMessage(score: number): string {
    if (score >= 90) return 'Tight tolerance work. The chips are flying — you\'ve got this.';
    if (score >= 70) return 'Solid pass. Take a tenth off and try the section again to lock it in.';
    return 'Back to the bench — review the lesson and re-cut. Mistakes here are cheaper than mistakes on stock.';
  }

  getScoreClass(score: number): string {
    if (score >= 90) return 'score-gold';
    if (score >= 70) return 'score-pass';
    return 'score-retry';
  }

  private resetQuiz() {
    this.currentQuestionIndex = 0;
    this.selectedAnswerIndex = null;
    this.showExplanation = false;
    this.quizAnswers = [];
    this.quizCompleted = false;
    this.quizScore = 0;
  }

  getExerciseProgress(exerciseId: string): ExerciseProgress | undefined {
    if (!this.selectedModule) return undefined;
    return this.userProgress[this.selectedModule.id]?.exercises?.[exerciseId];
  }

  isExerciseComplete(exerciseId: string): boolean {
    return !!this.getExerciseProgress(exerciseId)?.completedAt;
  }

  get exercisesCompleteCount(): number {
    if (!this.selectedModule?.practiceExercises?.length) return 0;
    return this.selectedModule.practiceExercises.filter(ex => this.isExerciseComplete(ex.id)).length;
  }

  toggleExercise(exerciseId: string) {
    if (!this.userId || !this.selectedModule) return;
    const path = `machining-progress/${this.userId}/${this.selectedModule.id}/exercises/${exerciseId}`;
    const existing = this.getExerciseProgress(exerciseId);

    if (existing?.completedAt) {
      // Marking incomplete. Keep notes if present (consult local draft, since
      // a recent keystroke may not have flushed to Firebase yet).
      const draftNotes = this.notesDrafts[exerciseId];
      if (draftNotes || existing.notes) {
        this.inCtx(() => this.db.object(path).update({ completedAt: null }));
      } else {
        this.inCtx(() => this.db.object(path).set(null));
      }
    } else {
      // Marking complete.
      this.inCtx(() => this.db.object(path).update({ completedAt: Date.now() }));
    }
  }

  onExerciseNotesChange(exerciseId: string, notes: string) {
    this.notesDrafts[exerciseId] = notes;
    if (this.notesTimeouts[exerciseId]) {
      clearTimeout(this.notesTimeouts[exerciseId]);
    }
    this.notesTimeouts[exerciseId] = setTimeout(() => {
      this.saveExerciseNotes(exerciseId);
      delete this.notesTimeouts[exerciseId];
    }, 500);
  }

  onExerciseNotesBlur(exerciseId: string) {
    if (this.notesTimeouts[exerciseId]) {
      clearTimeout(this.notesTimeouts[exerciseId]);
      delete this.notesTimeouts[exerciseId];
    }
    this.saveExerciseNotes(exerciseId);
  }

  private saveExerciseNotes(exerciseId: string) {
    if (!this.userId || !this.selectedModule) return;
    const notes = this.notesDrafts[exerciseId] || '';
    const path = `machining-progress/${this.userId}/${this.selectedModule.id}/exercises/${exerciseId}`;
    const existing = this.getExerciseProgress(exerciseId);

    if (notes) {
      this.inCtx(() => this.db.object(path).update({ notes }));
    } else if (existing?.completedAt) {
      this.inCtx(() => this.db.object(path).update({ notes: null }));
    } else {
      this.inCtx(() => this.db.object(path).set(null));
    }
  }

  formatCompletedDate(timestamp: number | undefined): string {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  machinePillLabel(machine: Machine | undefined): string {
    return machine ? machine.charAt(0).toUpperCase() + machine.slice(1) : 'Both';
  }
}
